import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useWorkspaceSettings } from '@/hooks/useWorkspaceSettings'
import { DATE_FORMATS, type WorkspaceSettings } from '@/services/api/workspace-settings.service'
import { getWorkspaceUsers } from '@/services/api/workspace.service'
import { useAuthStore } from '@/stores/auth.store'
import { parseApiError } from '@/utils/error-parser'

function GeneralForm({ settings, canEdit }: { settings: WorkspaceSettings; canEdit: boolean }) {
  const { update, isUpdating } = useWorkspaceSettings()
  const [name, setName] = useState(settings.name)
  const [dateFormat, setDateFormat] = useState(settings.dateFormat)
  const dirty = name.trim() !== settings.name || dateFormat !== settings.dateFormat

  return (
    <Card>
      <CardHeader>
        <CardTitle>General</CardTitle>
        <CardDescription>The name and date format everyone in this workspace sees.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-md space-y-1.5">
          <Label htmlFor="ws-name">Workspace name</Label>
          <Input id="ws-name" value={name} maxLength={120} disabled={!canEdit} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="max-w-md space-y-1.5">
          <Label>Date format</Label>
          <Select value={dateFormat} onValueChange={setDateFormat} disabled={!canEdit}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {DATE_FORMATS.map((f) => <SelectItem key={f} value={f}>{f.replace('dd', 'DD').replace('yyyy', 'YYYY')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {canEdit && (
          <Button
            disabled={!dirty || !name.trim() || isUpdating}
            onClick={() => update({ name: name.trim(), dateFormat }).then(() => toast.success('Settings saved'), (e) => toast.error(parseApiError(e).detail || 'Could not save'))}
          >
            Save changes
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

function TransferCard({ settings }: { settings: WorkspaceSettings }) {
  const { requestTransfer, isRequestingTransfer, cancelTransfer } = useWorkspaceSettings()
  const currentUserId = useAuthStore((s) => s.userId)
  const { data: members = [] } = useQuery({ queryKey: ['workspace-users'], queryFn: getWorkspaceUsers })
  const [target, setTarget] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const candidates = members.filter((m) => m.userId !== currentUserId)
  const targetMember = candidates.find((m) => m.userId === target)
  const pending = settings.pendingTransfer

  return (
    <Card>
      <CardHeader>
        <CardTitle>Transfer ownership</CardTitle>
        <CardDescription>
          The new owner gets an email with a link and becomes the owner once they accept. You stay on as a member with your roles.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {pending ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span>Waiting for <strong>{pending.toEmail ?? 'the new owner'}</strong> to accept (link expires {new Date(pending.expiresAt).toLocaleString()}).</span>
            <Button variant="outline" size="sm" onClick={() => void cancelTransfer().then(() => toast.success('Transfer cancelled'))}>Cancel transfer</Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-72 space-y-1.5">
              <Label>New owner</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger><SelectValue placeholder="Choose a member" /></SelectTrigger>
                <SelectContent>
                  {candidates.map((m) => <SelectItem key={m.userId} value={m.userId}>{m.name} ({m.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button disabled={!target || isRequestingTransfer} onClick={() => setConfirmOpen(true)}>Send transfer email</Button>
          </div>
        )}
      </CardContent>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Transfer ownership?"
        description={`We will email ${targetMember?.email ?? 'the new owner'} a link to accept. Nothing changes until they do.`}
        confirmLabel="Send email"
        pending={isRequestingTransfer}
        onConfirm={() => requestTransfer(target).then(
          () => { setConfirmOpen(false); setTarget(''); toast.success('Email sent') },
          (e) => toast.error(parseApiError(e).detail || 'Could not start the transfer'),
        )}
      />
    </Card>
  )
}

function DeleteCard({ settings }: { settings: WorkspaceSettings }) {
  const { requestDeletion, isRequestingDeletion } = useWorkspaceSettings()
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive">Delete workspace</CardTitle>
        <CardDescription>
          Removes the workspace for every member right away. We email you a link to confirm first; our team can restore a deleted workspace for a limited time.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="destructive" onClick={() => { setTyped(''); setOpen(true) }}>Delete this workspace…</Button>
      </CardContent>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Delete this workspace?"
        description={`Type "${settings.name}" to continue. We will then email you a confirmation link.`}
        confirmLabel="Email me the link"
        destructive
        pending={isRequestingDeletion}
        confirmDisabled={typed !== settings.name}
        onConfirm={() => requestDeletion().then(
          () => { setOpen(false); toast.success('Check your email to finish deleting the workspace') },
          (e) => toast.error(parseApiError(e).detail || 'Could not start the deletion'),
        )}
      >
        <Input aria-label="Workspace name" value={typed} onChange={(e) => setTyped(e.target.value)} />
      </ConfirmDialog>
    </Card>
  )
}

export function GeneralTab({ canEdit, isOwner }: { canEdit: boolean; isOwner: boolean }) {
  const { settings } = useWorkspaceSettings()
  if (!settings) return null
  return (
    <div className="space-y-4">
      <GeneralForm key={settings.name + settings.dateFormat} settings={settings} canEdit={canEdit} />
      {isOwner && <TransferCard settings={settings} />}
      {isOwner && <DeleteCard settings={settings} />}
    </div>
  )
}
